import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
}

const emptyForm = {
  id: null as number | null,
  username: '',
  email: '',
  role: 'viewer',
  password: '',
  is_active: true,
};

const UsersPage: React.FC = () => {
  const [items, setItems] = useState<User[]>([]);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = () => {
    apiClient.get('/users').then(res => {
      setItems(res.data.data.items as User[]);
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const startCreate = () => {
    setError(null);
    setForm(emptyForm);
  };

  const startEdit = (user: User) => {
    setError(null);
    setForm({
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role,
      password: '',
      is_active: user.is_active,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target;
    const { name } = target;

    let value: string | boolean = (target as HTMLInputElement | HTMLSelectElement).value;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      value = target.checked;
    }

    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (form.id == null) {
        // Create
        await apiClient.post('/users', {
          username: form.username,
          email: form.email,
          role: form.role,
          password: form.password,
          is_active: form.is_active,
        });
      } else {
        // Update
        const payload: any = {
          username: form.username,
          email: form.email,
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password) {
          payload.password = form.password;
        }
        await apiClient.put(`/users/${form.id}`, payload);
      }

      setForm(emptyForm);
      loadUsers();
    } catch (err: any) {
      setError('Error guardando el usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (user: User) => {
    if (!window.confirm(`Desactivar usuario ${user.username}?`)) {
      return;
    }
    try {
      await apiClient.delete(`/users/${user.id}`);
      loadUsers();
    } catch (err) {
      alert('No se pudo desactivar el usuario');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Usuarios
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Gestiona el acceso de los usuarios al sistema y sus roles.
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nuevo usuario' : `Editar usuario #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nuevo
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Usuario
              </label>
              <input
                name="username"
                value={form.username}
                onChange={handleChange}
                required
                placeholder="Nombre de usuario"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="usuario@empresa.com"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Rol
              </label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                }}
              >
                <option value="admin">admin</option>
                <option value="supervisor">supervisor</option>
                <option value="auditor">auditor</option>
                <option value="technician">technician</option>
                <option value="viewer">viewer</option>
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Password {form.id != null && '(deja en blanco para no cambiar)'}
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                {...(form.id == null ? { required: true } : {})}
                placeholder={form.id == null ? 'Contraseña inicial' : '********'}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                Activo
              </label>
            </div>

            {error && (
              <div
                style={{
                  color: '#fecaca',
                  background: 'rgba(220, 38, 38, 0.12)',
                  border: '1px solid rgba(248, 113, 113, 0.4)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  fontSize: 12,
                  marginBottom: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '8px 14px',
                borderRadius: 999,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                boxShadow: loading
                  ? '0 0 0 rgba(0,0,0,0)'
                  : '0 12px 30px rgba(22, 163, 74, 0.55)',
                opacity: loading ? 0.9 : 1,
              }}
            >
              {loading ? 'Guardando...' : 'Guardar usuario'}
            </button>
          </form>
        </section>

        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de usuarios
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Total: {items.length}</span>
          </div>

          {items.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
              No hay usuarios registrados.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Usuario</th>
                    <th style={{ padding: '6px 4px' }}>Email</th>
                    <th style={{ padding: '6px 4px' }}>Rol</th>
                    <th style={{ padding: '6px 4px' }}>Activo</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(user => (
                    <tr
                      key={user.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{user.id}</td>
                      <td style={{ padding: '6px 4px' }}>{user.username}</td>
                      <td style={{ padding: '6px 4px' }}>{user.email}</td>
                      <td style={{ padding: '6px 4px' }}>{user.role}</td>
                      <td style={{ padding: '6px 4px' }}>{user.is_active ? 'Sí' : 'No'}</td>
                      <td style={{ padding: '6px 4px' }}>
                        <button
                          type="button"
                          onClick={() => startEdit(user)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(148, 163, 184, 0.7)',
                            backgroundColor: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 11,
                            cursor: 'pointer',
                            marginRight: 4,
                          }}
                        >
                          Editar
                        </button>
                        {user.is_active && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(user)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(248, 113, 113, 0.6)',
                              backgroundColor: 'rgba(127, 29, 29, 0.2)',
                              color: '#fecaca',
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Desactivar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default UsersPage;
